// Team Planner Zod schemas. Mirrors the Driicut planner shapes but with
// pg-friendly types (date-as-Date, integer ids for users where the BSP
// schema uses serial). Kept in one file to stay under the 150-line cap
// the project follows everywhere else; split if it grows past that.

import { z } from "zod";
import { PLANNER_ITEM_KINDS } from "./const";

// ── Common hydrated blocks ────────────────────────────────────────────
export const PlannerOwnerSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  entraObjectId: z.string().nullable(),
});
export type PlannerOwner = z.infer<typeof PlannerOwnerSchema>;

export const PlannerTeamSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  monogram: z.string().nullable(),
  colourToken: z.string().nullable(),
});
export type PlannerTeamSummary = z.infer<typeof PlannerTeamSummarySchema>;

// ── Quarter ───────────────────────────────────────────────────────────
export const QuarterSchema = z.string().regex(/^\d{4}-Q[1-4]$/);

// ── Rocks ─────────────────────────────────────────────────────────────
export const ROCK_STATUS = [
  "draft",
  "on_track",
  "off_track",
  "done",
  "missed",
  "cancelled",
] as const;
export const ROCK_PROGRESS_TYPE = [
  "status_only",
  "milestones",
  "numeric_target",
] as const;
export const ROCK_PROGRESS_MODE = ["auto", "manual"] as const;
export const ROCK_STATUS_UPDATE_RAG = ["on_track", "at_risk", "off_track"] as const;

export const RockSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  quarter: QuarterSchema,
  startAt: z.date().nullable(),
  committedAt: z.date().nullable(),
  dueAt: z.date(),
  status: z.enum(ROCK_STATUS),
  progressType: z.enum(ROCK_PROGRESS_TYPE),
  progressPercent: z.number().int().min(0).max(100),
  progressMode: z.enum(ROCK_PROGRESS_MODE),
  progressOverride: z.number().int().min(0).max(100).nullable(),
  fromIssueId: z.string().uuid().nullable(),
  parentTeamId: z.string().uuid().nullable(),
  parentTeam: PlannerTeamSummarySchema.nullable(),
  isCompanyRock: z.boolean(),
  labels: z.array(z.string()).nullable(),
  createdByUserId: z.string().uuid(),
  primaryOwnerUserId: z.string().uuid().nullable(),
  primaryOwner: PlannerOwnerSchema.nullable(),
  watchers: z.array(PlannerOwnerSchema),
  watcherCount: z.number().int().nonnegative(),
  seq: z.number().int().nonnegative(),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable(),
  milestoneCounts: z.object({ total: z.number().int(), done: z.number().int() }),
});
export type Rock = z.infer<typeof RockSchema>;

export const CreateRockInput = z.object({
  title: z.string().min(1).max(300),
  quarter: QuarterSchema,
  dueAt: z.coerce.date(),
  startAt: z.coerce.date().optional(),
  description: z.string().max(10_000).optional(),
  isCompanyRock: z.boolean().optional(),
  parentTeamId: z.string().uuid().nullable().optional(),
  fromIssueId: z.string().uuid().nullable().optional(),
  status: z.enum(ROCK_STATUS).optional(),
});
export const UpdateRockInput = z.object({
  title: z.string().min(1).max(300).optional(),
  description: z.string().max(10_000).nullable().optional(),
  quarter: QuarterSchema.optional(),
  startAt: z.coerce.date().nullable().optional(),
  dueAt: z.coerce.date().optional(),
  isCompanyRock: z.boolean().optional(),
  parentTeamId: z.string().uuid().nullable().optional(),
  progressMode: z.enum(ROCK_PROGRESS_MODE).optional(),
  progressOverride: z.number().int().min(0).max(100).nullable().optional(),
});
export const SetRockStatusInput = z.object({ status: z.enum(ROCK_STATUS) });
export const PostRockStatusUpdateInput = z.object({
  rag: z.enum(ROCK_STATUS_UPDATE_RAG),
  note: z.string().min(1).max(2_000),
});

// ── Milestones ────────────────────────────────────────────────────────
export const MILESTONE_STATUS = ["open", "in_progress", "blocked", "done", "cancelled"] as const;

export const MilestoneSchema = z.object({
  id: z.string().uuid(),
  rockId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  dueAt: z.date(),
  status: z.enum(MILESTONE_STATUS),
  sortOrder: z.number().int(),
  createdByUserId: z.string().uuid(),
  primaryOwnerUserId: z.string().uuid().nullable(),
  completedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Milestone = z.infer<typeof MilestoneSchema>;

export const AddMilestoneInput = z.object({
  rockId: z.string().uuid(),
  title: z.string().min(1).max(300),
  dueAt: z.coerce.date(),
  description: z.string().max(10_000).optional(),
});
export const UpdateMilestoneInput = z.object({
  title: z.string().min(1).max(300).optional(),
  description: z.string().max(10_000).nullable().optional(),
  dueAt: z.coerce.date().optional(),
  status: z.enum(MILESTONE_STATUS).optional(),
});

// ── Todos ─────────────────────────────────────────────────────────────
export const TODO_STATUS = ["open", "in_progress", "blocked", "done", "cancelled"] as const;
export const TODO_PRIORITY = ["low", "medium", "high", "critical"] as const;
export const TODO_OWNER_TYPE = ["individual", "team"] as const;

export const SubtaskSchema = z.object({
  id: z.string().uuid(),
  todoId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  status: z.enum(TODO_STATUS),
  sortOrder: z.number().int(),
  dueAt: z.date().nullable(),
  createdByUserId: z.string().uuid(),
  primaryOwnerUserId: z.string().uuid().nullable(),
  completedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  ownerOverride: z.boolean(),
  dueOverride: z.boolean(),
});
export type Subtask = z.infer<typeof SubtaskSchema>;

export const TodoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  status: z.enum(TODO_STATUS),
  priority: z.enum(TODO_PRIORITY),
  ownerType: z.enum(TODO_OWNER_TYPE),
  isPrivate: z.boolean(),
  dueAt: z.date().nullable(),
  rockId: z.string().uuid().nullable(),
  rockTitle: z.string().nullable(),
  milestoneId: z.string().uuid().nullable(),
  milestoneTitle: z.string().nullable(),
  issueId: z.string().uuid().nullable(),
  parentTeamId: z.string().uuid().nullable(),
  labels: z.array(z.string()).nullable(),
  createdByUserId: z.string().uuid(),
  primaryOwnerUserId: z.string().uuid().nullable(),
  primaryOwnerTeamId: z.string().uuid().nullable(),
  primaryOwner: PlannerOwnerSchema.nullable(),
  primaryOwnerTeam: PlannerTeamSummarySchema.nullable(),
  coOwnerUserIds: z.array(z.string().uuid()),
  coOwners: z.array(PlannerOwnerSchema),
  watcherUserIds: z.array(z.string().uuid()),
  watchers: z.array(PlannerOwnerSchema),
  watcherCount: z.number().int().nonnegative(),
  seq: z.number().int().nonnegative(),
  subtaskCounts: z.object({ total: z.number().int(), done: z.number().int() }),
  inlineSubtasks: z.array(SubtaskSchema),
  commentCount: z.number().int(),
  completedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Todo = z.infer<typeof TodoSchema>;

export const CreateTodoInput = z.object({
  title: z.string().min(1).max(300),
  description: z.string().max(10_000).optional(),
  priority: z.enum(TODO_PRIORITY).optional(),
  status: z.enum(TODO_STATUS).optional(),
  dueAt: z.coerce.date().nullable().optional(),
  rockId: z.string().uuid().nullable().optional(),
  milestoneId: z.string().uuid().nullable().optional(),
  labels: z.array(z.string().min(1).max(64)).max(10).optional(),
  parentTeamId: z.string().uuid().nullable().optional(),
  ownerType: z.enum(TODO_OWNER_TYPE).optional(),
  isPrivate: z.boolean().optional(),
});
export const UpdateTodoInput = z.object({
  title: z.string().min(1).max(300).optional(),
  description: z.string().max(10_000).nullable().optional(),
  priority: z.enum(TODO_PRIORITY).optional(),
  dueAt: z.coerce.date().nullable().optional(),
  rockId: z.string().uuid().nullable().optional(),
  milestoneId: z.string().uuid().nullable().optional(),
  labels: z.array(z.string().min(1).max(64)).max(10).nullable().optional(),
  isPrivate: z.boolean().optional(),
});
export const SetTodoStatusInput = z.object({ status: z.enum(TODO_STATUS) });

export const AddSubtaskInput = z.object({
  title: z.string().min(1).max(300),
  description: z.string().max(10_000).optional(),
  dueAt: z.coerce.date().nullable().optional(),
  ownerUserId: z.string().uuid().optional(),
});
export const UpdateSubtaskInput = z.object({
  title: z.string().min(1).max(300).optional(),
  description: z.string().max(10_000).nullable().optional(),
  dueAt: z.coerce.date().nullable().optional(),
  status: z.enum(TODO_STATUS).optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
});

// ── Issues / Opportunities ────────────────────────────────────────────
export const ISSUE_STATUS = ["open", "discussing", "actioned", "converted", "archived"] as const;
export const ISSUE_PRIORITY = ["low", "medium", "high", "critical"] as const;
export const ISSUE_TERM = ["short_term", "long_term"] as const;

export const IssueSchema = z.object({
  id: z.string().uuid(),
  seq: z.number().int().nonnegative(),
  title: z.string(),
  shortDescription: z.string().nullable(),
  story: z.string().nullable(),
  status: z.enum(ISSUE_STATUS),
  term: z.enum(ISSUE_TERM),
  priority: z.enum(ISSUE_PRIORITY),
  isAnonymous: z.boolean(),
  homeTeamId: z.string().uuid().nullable(),
  homeTeam: PlannerTeamSummarySchema.nullable(),
  voteCount: z.number().int(),
  raisedByUserId: z.string().uuid().nullable(),
  raisedBy: PlannerOwnerSchema.nullable(),
  raisedAt: z.date(),
  convertedToRockId: z.string().uuid().nullable(),
  convertedToTodoId: z.string().uuid().nullable(),
  resolvedAt: z.date().nullable(),
  resolutionNotes: z.string().nullable(),
  labels: z.array(z.string()).nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  archivedAt: z.date().nullable(),
  viewerHasVoted: z.boolean(),
});
export type Issue = z.infer<typeof IssueSchema>;

export const RaiseIssueInput = z.object({
  title: z.string().min(1).max(300),
  shortDescription: z.string().max(280).nullable().optional(),
  story: z.string().max(10_000).nullable().optional(),
  priority: z.enum(ISSUE_PRIORITY).optional(),
  term: z.enum(ISSUE_TERM).optional(),
  isAnonymous: z.boolean().optional(),
  homeTeamId: z.string().uuid().nullable().optional(),
  labels: z.array(z.string().min(1).max(64)).max(10).optional(),
});
export const UpdateIssueInput = z.object({
  title: z.string().min(1).max(300).optional(),
  shortDescription: z.string().max(280).nullable().optional(),
  story: z.string().max(10_000).nullable().optional(),
  priority: z.enum(ISSUE_PRIORITY).optional(),
  term: z.enum(ISSUE_TERM).optional(),
  isAnonymous: z.boolean().optional(),
  homeTeamId: z.string().uuid().nullable().optional(),
  labels: z.array(z.string().min(1).max(64)).max(10).nullable().optional(),
  resolutionNotes: z.string().max(10_000).nullable().optional(),
});
export const SetIssueStatusInput = z.object({ status: z.enum(ISSUE_STATUS) });
export const ConvertIssueToTodoInput = z.object({
  dueAt: z.coerce.date().nullable().optional(),
  rockId: z.string().uuid().nullable().optional(),
});
export const ConvertIssueToRockInput = z.object({
  quarter: QuarterSchema,
  dueAt: z.coerce.date(),
});

// ── Teams ─────────────────────────────────────────────────────────────
export const TEAM_SOURCE = ["department", "custom"] as const;

export const TeamSummarySchema = z.object({
  id: z.string().uuid(),
  source: z.enum(TEAM_SOURCE),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  monogram: z.string().nullable(),
  colourToken: z.string().nullable(),
  projectCode: z.string().nullable(),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
  createdByEntraId: z.string().nullable(),
  isPrivate: z.boolean(),
  isActive: z.boolean(),
  archivedAt: z.date().nullable(),
  memberCount: z.number().int(),
  createdAt: z.date(),
});
export type TeamSummary = z.infer<typeof TeamSummarySchema>;

export const TeamMemberSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  userId: z.string().uuid(),
  role: z.enum(["member", "lead"]),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  entraObjectId: z.string().nullable(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const CreateTeamInput = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(10_000).optional(),
  monogram: z.string().min(1).max(4).optional(),
  colourToken: z.string().max(32).optional(),
  projectCode: z.string().max(64).optional(),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  memberUserIds: z.array(z.string().uuid()).optional(),
  leadUserIds: z.array(z.string().uuid()).optional(),
  isPrivate: z.boolean().optional(),
});
export const UpdateTeamInput = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(10_000).nullable().optional(),
  monogram: z.string().min(1).max(4).optional(),
  colourToken: z.string().max(32).optional(),
  projectCode: z.string().max(64).nullable().optional(),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  isPrivate: z.boolean().optional(),
});
export const TeamMemberChangeInput = z.object({
  userId: z.string().uuid(),
  action: z.enum(["add", "remove", "set_lead", "unset_lead"]),
});

// ── Comments ──────────────────────────────────────────────────────────
export const CommentSchema = z.object({
  id: z.string().uuid(),
  itemKind: z.enum(PLANNER_ITEM_KINDS),
  itemId: z.string().uuid(),
  authorUserId: z.string().uuid(),
  authorDisplayName: z.string().nullable(),
  authorEntraObjectId: z.string().nullable(),
  body: z.string(),
  mentions: z.array(z.string().uuid()),
  mentionDisplayNames: z.array(z.string()),
  editedAt: z.date().nullable(),
  deletedAt: z.date().nullable(),
  createdAt: z.date(),
});
export type Comment = z.infer<typeof CommentSchema>;

export const PostCommentInput = z.object({
  body: z.string().min(1).max(10_000),
  mentions: z.array(z.string().uuid()).max(20).optional(),
});
export const EditCommentInput = z.object({
  body: z.string().min(1).max(10_000),
  mentions: z.array(z.string().uuid()).max(20).optional(),
});

// ── Participants ──────────────────────────────────────────────────────
export const ParticipantChangeInput = z.object({
  role: z.enum(["co_owner", "watcher"]),
  subjectType: z.enum(["user", "team"]),
  subjectId: z.string().uuid(),
  action: z.enum(["add", "remove"]),
});

// ── Me context / Stats ────────────────────────────────────────────────
export const PlannerUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  displayName: z.string().nullable(),
  role: z.enum(["user", "admin", "management"]),
  entraObjectId: z.string().nullable(),
});

export const PlannerTeamLiteSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  source: z.enum(TEAM_SOURCE),
  monogram: z.string().nullable(),
  isPrivate: z.boolean(),
});

export const MeContextSchema = z.object({
  user: PlannerUserSchema,
  myTeams: z.array(PlannerTeamLiteSchema),
  canSeeAllScope: z.boolean(),
});
export type MeContext = z.infer<typeof MeContextSchema>;

export const PlannerStatsSchema = z.object({
  activeRocks: z.number().int().nonnegative(),
  openTodos: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  opportunities: z.number().int().nonnegative(),
});
export type PlannerStats = z.infer<typeof PlannerStatsSchema>;
